import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Envelope, Send } from 'react-bootstrap-icons';
import { adminEmailTemplatesAPI } from '../services/adminApi';

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    description: '',
    is_active: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await adminEmailTemplatesAPI.getAll();
      setTemplates(response.data);
      if (response.data.length > 0) {
        handleSelectTemplate(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      description: template.description,
      is_active: template.is_active
    });
    setEditMode(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await adminEmailTemplatesAPI.update(selectedTemplate.id, formData);
      setMessage({ type: 'success', text: 'Vorlage erfolgreich aktualisiert!' });
      setEditMode(false);
      loadTemplates();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Speichern!' });
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Bitte Email-Adresse eingeben!' });
      return;
    }
    
    try {
      await adminEmailTemplatesAPI.sendTest(selectedTemplate.id, testEmail);
      setMessage({ type: 'success', text: 'Test-Email erfolgreich versendet!' });
      setShowTestModal(false);
      setTestEmail('');
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Fehler beim Versenden!' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                👑 Admin Panel
              </h1>
              <nav className="flex space-x-4">
                <button onClick={() => navigate('/admin')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">Dashboard</button>
                <button onClick={() => navigate('/admin/packages')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">Pakete</button>
                <button onClick={() => navigate('/admin/courses')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">Kurse</button>
                <button onClick={() => navigate('/admin/users')} className="px-4 py-2 text-gray-700 hover:text-primary-600 font-medium">User</button>
                <button onClick={() => navigate('/admin/emails')} className="px-4 py-2 text-primary-600 font-bold border-b-2 border-primary-600">Email-Vorlagen</button>
              </nav>
            </div>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium">
              ← Zurück
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Email-Vorlagen</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Templates List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Envelope className="mr-2" size={24} />
                Vorlagen
              </h3>
              <div className="space-y-2">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={`w-full text-left p-4 rounded-lg transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{template.name}</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        selectedTemplate?.id === template.id
                          ? 'bg-white/20 text-white'
                          : template.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {template.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    <p className={`text-sm ${selectedTemplate?.id === template.id ? 'text-white/90' : 'text-gray-600'}`}>
                      {template.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Template Editor */}
          <div className="lg:col-span-2">
            {selectedTemplate ? (
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">{selectedTemplate.name}</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowTestModal(true)}
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium flex items-center space-x-2"
                    >
                      <Send size={16} />
                      <span>Test senden</span>
                    </button>
                    {!editMode ? (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-4 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 font-medium"
                      >
                        Bearbeiten
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setEditMode(false);
                          handleSelectTemplate(selectedTemplate);
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                      >
                        Abbrechen
                      </button>
                    )}
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800 font-medium mb-2">📝 Verfügbare Platzhalter:</p>
                  <p className="text-xs text-blue-700 font-mono">{selectedTemplate.placeholders}</p>
                </div>

                {editMode ? (
                  <form onSubmit={handleUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email-Body (HTML)</label>
                      <textarea
                        value={formData.body}
                        onChange={(e) => setFormData({...formData, body: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                        rows="15"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        rows="2"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                        Vorlage aktiv
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="w-full px-4 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700"
                    >
                      Speichern
                    </button>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Betreff</p>
                      <p className="text-lg font-medium text-gray-900">{selectedTemplate.subject}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Beschreibung</p>
                      <p className="text-gray-700">{selectedTemplate.description}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Email-Vorschau</p>
                      <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 max-h-96 overflow-y-auto">
                        <div dangerouslySetInnerHTML={{ __html: selectedTemplate.body }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <Envelope size={64} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Wähle eine Vorlage aus</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Test-Email senden</h3>
            <p className="text-gray-600 mb-4">
              Die Email wird mit Test-Daten versendet.
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email-Adresse</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="test@example.com"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {setShowTestModal(false); setTestEmail('');}}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSendTest}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-secondary-700 flex items-center justify-center space-x-2"
              >
                <Send size={16} />
                <span>Senden</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailTemplates;
